// src/components/common/AIPanel.tsx
import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { RefreshCw, Sparkles, Phone, Mail, Linkedin, Clock, Ban } from 'lucide-react';
import { useLeadAIPanel, useReEnrichLead, useApproveEmail } from '@hooks/useCRM';

const ACTION_ICONS: Record<string, ReactNode> = {
  call: <Phone size={14} />,
  email: <Mail size={14} />,
  linkedin: <Linkedin size={14} />,
  wait: <Clock size={14} />,
  disqualify: <Ban size={14} />,
};

function scoreColor(score: number | null | undefined): string {
  if (score == null) return 'pill-neutral';
  if (score >= 70) return 'pill-high';
  if (score >= 40) return 'pill-medium';
  return 'pill-low';
}

type AIPanelProps = {
  leadId: string;
};

export function AIPanel({ leadId }: AIPanelProps) {
  const { data: panel, isLoading } = useLeadAIPanel(leadId);
  const reEnrichMutation = useReEnrichLead();
  const approveEmailMutation = useApproveEmail();

  const [emailOpen, setEmailOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const draftSubject = panel?.ai_draft_email_subject ?? '';
  const draftBody = panel?.ai_draft_email_body ?? '';

  function handleOpenEmail() {
    setSubject(draftSubject);
    setBody(draftBody);
    setEmailOpen(true);
  }

  async function handleReEnrich() {
    await reEnrichMutation.mutateAsync(leadId);
  }

  async function handleApprove() {
    await approveEmailMutation.mutateAsync({
      leadId,
      payload: { subject: subject || draftSubject, body: body || draftBody },
    });
    setEmailOpen(false);
  }

  const isPending = isLoading || panel?.ai_enrichment_status === 'pending' || (!panel && !isLoading);
  const isFailed = panel?.ai_enrichment_status === 'failed';
  const isDone = panel?.ai_enrichment_status === 'done';

  // Trigger re-enrich on mount if never enriched
  useEffect(() => {
    if (!isLoading && panel && panel.ai_enrichment_status == null) {
      reEnrichMutation.mutate(leadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, isLoading, panel?.ai_enrichment_status]);

  return (
    <div className="ai-panel">
      <div className="subsection-header">
        <div>
          <h3 className="ai-panel-title">
            <Sparkles size={16} />
            AI Analysis
          </h3>
        </div>
        <button
          type="button"
          className="button button-secondary"
          onClick={handleReEnrich}
          disabled={reEnrichMutation.isPending || isPending}
        >
          <RefreshCw size={14} />
          Refresh AI
        </button>
      </div>

      {isPending && !isFailed && (
        <div className="ai-panel-skeleton">
          <p className="muted-copy">AI is analyzing this lead…</p>
          <div className="skeleton-line skeleton-line-short" />
          <div className="skeleton-line" />
          <div className="skeleton-line skeleton-line-medium" />
        </div>
      )}

      {isFailed && (
        <div className="ai-panel-error">
          <p>AI enrichment failed.</p>
          <button
            type="button"
            className="button button-secondary"
            onClick={handleReEnrich}
            disabled={reEnrichMutation.isPending}
          >
            <RefreshCw size={14} />
            Refresh AI
          </button>
        </div>
      )}

      {isDone && panel && (
        <div className="ai-panel-content">
          <div className="ai-score-row">
            {panel.ai_score != null && (
              <span className={`pill ${scoreColor(panel.ai_score)}`}>
                {Math.round(panel.ai_score)} / 100
              </span>
            )}
            {panel.ai_next_action && (
              <span className="pill pill-neutral ai-action-pill">
                {ACTION_ICONS[panel.ai_next_action]}
                {panel.ai_next_action}
              </span>
            )}
          </div>

          {panel.ai_score_reasoning && (
            <p className="ai-reasoning">{panel.ai_score_reasoning}</p>
          )}

          {panel.ai_next_action_note && (
            <p className="ai-action-note muted-copy">{panel.ai_next_action_note}</p>
          )}

          {draftSubject && (
            <div className="ai-email-section">
              {!emailOpen ? (
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={handleOpenEmail}
                >
                  <Mail size={14} />
                  View Draft Email
                </button>
              ) : (
                <div className="ai-email-draft">
                  <label className="form-field">
                    <span>Subject</span>
                    <input value={subject} onChange={(e) => setSubject(e.target.value)} />
                  </label>
                  <label className="form-field">
                    <span>Body</span>
                    <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
                  </label>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="button button-primary"
                      onClick={handleApprove}
                      disabled={approveEmailMutation.isPending}
                    >
                      {approveEmailMutation.isPending ? 'Saving…' : 'Approve Email'}
                    </button>
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={() => {
                        setEmailOpen(false);
                        approveEmailMutation.reset();
                      }}
                    >
                      Close
                    </button>
                  </div>
                  {approveEmailMutation.isSuccess && (
                    <p className="success-copy">Email approved and logged to timeline.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
